/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDirectoryReviewItemsRequest,
  ListDirectoryReviewItemsSecurity,
} from "../models/operations/listdirectoryreviewitems.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDirectoryReviewItemsQuery,
  DirectoryReviewItemsQueryData,
  prefetchDirectoryReviewItems,
  queryKeyDirectoryReviewItems,
} from "./directoryReviewItems.core.js";
export {
  buildDirectoryReviewItemsQuery,
  type DirectoryReviewItemsQueryData,
  prefetchDirectoryReviewItems,
  queryKeyDirectoryReviewItems,
};

export type DirectoryReviewItemsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDirectoryReviewItems access
 *
 * @remarks
 * List the directory changes waiting for an admin decision, oldest first: a directory group first seen after review started, with no mapping; a new value of an attribute key that already has mappings; and a mapped group or attribute value that has gone from the directory. Each item carries the same proposal and version as previewDirectoryRoleSetup, for resolveDirectoryReviewItem. Nothing is written.
 */
export function useDirectoryReviewItems(
  request?: ListDirectoryReviewItemsRequest | undefined,
  security?: ListDirectoryReviewItemsSecurity | undefined,
  options?: QueryHookOptions<
    DirectoryReviewItemsQueryData,
    DirectoryReviewItemsQueryError
  >,
): UseQueryResult<
  DirectoryReviewItemsQueryData,
  DirectoryReviewItemsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDirectoryReviewItemsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDirectoryReviewItems access
 *
 * @remarks
 * List the directory changes waiting for an admin decision, oldest first: a directory group first seen after review started, with no mapping; a new value of an attribute key that already has mappings; and a mapped group or attribute value that has gone from the directory. Each item carries the same proposal and version as previewDirectoryRoleSetup, for resolveDirectoryReviewItem. Nothing is written.
 */
export function useDirectoryReviewItemsSuspense(
  request?: ListDirectoryReviewItemsRequest | undefined,
  security?: ListDirectoryReviewItemsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DirectoryReviewItemsQueryData,
    DirectoryReviewItemsQueryError
  >,
): UseSuspenseQueryResult<
  DirectoryReviewItemsQueryData,
  DirectoryReviewItemsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDirectoryReviewItemsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDirectoryReviewItemsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DirectoryReviewItemsQueryData,
): DirectoryReviewItemsQueryData | undefined {
  const key = queryKeyDirectoryReviewItems(...queryKeyBase);

  return client.setQueryData<DirectoryReviewItemsQueryData>(key, data);
}

export function invalidateDirectoryReviewItems(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listDirectoryReviewItems",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDirectoryReviewItems(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listDirectoryReviewItems"],
  });
}
